import React, { useRef, useState } from 'react';
import { FunnelPlus, ListFilterPlus, Plus, Search } from 'lucide-react';
import { useFloating, offset, flip, shift, autoUpdate } from '@floating-ui/react';
import { AnimatePresence, motion } from 'framer-motion';
import { useClickAway } from 'react-use';
import TooltipInfo from '@components/TooltipInfo';

const SmartDropdown = ({ filteredFields = [],filters=[], addFilter, getColumnLabel }) => {
  const [showAddField, setShowAddField] = useState(false);
  const [searchField, setSearchField] = useState('');
  const [focusedIndex, setFocusedIndex] = useState(-1);
  
  const {
    x,
    y,
    reference,
    floating,
    strategy,
    refs,
    update,
  } = useFloating({
    open: showAddField,
    onOpenChange: setShowAddField,
    middleware: [
      offset(6),
      flip({
        fallbackPlacements: ['top-start', 'top-end', 'bottom-end', 'left-start', 'right-start'],
        crossAxis: true,
        padding: 8
      }),
      shift({ padding: 8 }),
    ],
    placement: 'bottom-start',
  });
const selectedFieldKeys = React.useMemo(() => {
  return new Set(
    (filters || [])
      .map(f => f?.columnName ?? f?.column ?? f?.defination?.Field ?? f?.Field)
      .filter(Boolean)
      .map(String)
  );
}, [filters]);

  // Auto-update position on scroll/resize
  React.useEffect(() => {
    if (refs.reference.current && refs.floating.current && showAddField) {
      return autoUpdate(refs.reference.current, refs.floating.current, update);
    }
  }, [showAddField, refs.reference, refs.floating, update]);

  const wrapperRef = useRef(null);
  useClickAway(wrapperRef, () => {
    if (showAddField) {
      setShowAddField(false);
    }
  });

  // const visibleFields = filteredFields.filter((f) =>
  //   getColumnLabel(f.Field).toLowerCase().includes(searchField.toLowerCase())
  // );
//   const visibleFields = filteredFields.filter((f) => {
//   const search = searchField.toLowerCase();

//   // Support both core fields (f.Field) and custom fields (f.column_name or f.label)
//   const name = f.Field || f.column_name || '';
//   const label = f.label || f.fieldLabel || name;
//   const ulable = getColumnLabel(label);
//   return ulable.toLowerCase().includes(search) || name.toLowerCase().includes(search);
// });

// Filter only by search (NOT by selected), then mark disabled
const visibleFields = (filteredFields || [])
  .filter((f) => {
    const key = String(f?.Field ?? f?.column_name ?? '');
    if (!key) return false;

    const q = searchField.trim().toLowerCase();
    if (!q) return true;

    const labelSrc = f?.label ?? f?.fieldLabel ?? key;
    const label = (getColumnLabel(labelSrc) || '').toLowerCase();
    return label.includes(q) || key.toLowerCase().includes(q);
  })
  .map((f) => {
    const key = String(f?.Field ?? f?.column_name ?? '');
    return { ...f, __key: key, __disabled: selectedFieldKeys.has(key) };
  });

// keyboard nav should skip disabled items
const firstEnabledIndex = React.useMemo(() => {
  return visibleFields.findIndex(f => !f.__disabled);
}, [visibleFields]);

const lastEnabledIndex = React.useMemo(() => {
  for (let i = visibleFields.length - 1; i >= 0; i--) {
    if (!visibleFields[i].__disabled) return i;
  }
  return -1;
}, [visibleFields]);

const nextEnabled = (start, dir) => {
  if (!visibleFields.length) return -1;
  for (let step = 1; step <= visibleFields.length; step++) {
    const idx = (start + dir * step + visibleFields.length) % visibleFields.length;
    if (!visibleFields[idx].__disabled) return idx;
  }
  return -1;
};
const handleKeyDown = (e) => {
  if (!showAddField || visibleFields.length === 0) return;

  if (e.key === 'ArrowDown') {
    e.preventDefault();
    setFocusedIndex(prev => {
      if (prev < 0) return firstEnabledIndex;
      const idx = nextEnabled(prev, +1);
      return idx >= 0 ? idx : prev;
    });
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    setFocusedIndex(prev => {
      if (prev < 0) return lastEnabledIndex;
      const idx = nextEnabled(prev, -1);
      return idx >= 0 ? idx : prev;
    });
  } else if (e.key === 'Enter' && focusedIndex >= 0) {
    const f = visibleFields[focusedIndex];
    if (!f.__disabled && f.__key) {
      addFilter(f.__key);
      setShowAddField(false);
    }
  }
};
  // const handleKeyDown = (e) => {
  //   if (!showAddField || visibleFields.length === 0) return;

  //   if (e.key === 'ArrowDown') {
  //     e.preventDefault();
  //     setFocusedIndex((prev) => (prev + 1) % visibleFields.length);
  //   } else if (e.key === 'ArrowUp') {
  //     e.preventDefault();
  //     setFocusedIndex((prev) => (prev - 1 + visibleFields.length) % visibleFields.length);
  //   } else if (e.key === 'Enter' && focusedIndex >= 0) {
  //     addFilter(visibleFields[focusedIndex].Field);
  //     setShowAddField(false);
  //   }
  // };
  
  return (
    <div ref={wrapperRef} className="relative" onKeyDown={handleKeyDown}>
       <TooltipInfo text="Add Columns to filter the data">
      <button
        ref={reference}
        onClick={() => setShowAddField((prev) => !prev)}
        className="p-2 bg-gray-100 rounded"
      >
        <FunnelPlus size={16} />
      </button>
      </TooltipInfo>
      <AnimatePresence>
        {showAddField && (
          <motion.div
            ref={floating}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            style={{ position: strategy, transform: `translate(${x ?? 0}px, ${y ?? 0}px)` }}
            className="w-72 bg-white border rounded shadow z-30"
          >
            <div className="relative">
              <input
                type="text"
                placeholder="Search field"
                value={searchField}
                onChange={(e) => {
                  setSearchField(e.target.value);
                  setFocusedIndex(-1);
                }}
                className="px-3 py-2 pr-10 w-full bg-gray-100 text-sm outline-none focus:outline-none"
              />
              <Search size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
            </div>
            <div className="max-h-60 overflow-y-auto divide-y">
              {/* {visibleFields.map((field, i) => (
                <div key={i} className={`p-2 px-3 text-sm cursor-pointer transition-colors duration-150 ${
                    i === focusedIndex ? 'bg-primaryLight text-primary' : 'hover:bg-gray-100 hover:text-primary'
                  }`}
                  onClick={() => {
                    addFilter(field); 
                    setShowAddField(false);
                  }}
                >
                  {field?.fieldID ? getColumnLabel(field.fieldLabel) : getColumnLabel(field.Field)}
                </div>
              ))} */}
              {visibleFields.map((field, i) => {
    const isFocused = i === focusedIndex;
    const isDisabled = field.__disabled;
    const base =
      "p-2 px-3 text-sm transition-colors duration-150 flex items-center justify-between";
    const enabledStyles = isFocused
      ? "bg-primaryLight text-primary cursor-pointer"
      : "hover:bg-gray-100 hover:text-primary cursor-pointer";
    const disabledStyles = "opacity-50 cursor-not-allowed";

    return (
      <div
        key={field.__key || i}
        aria-disabled={isDisabled}
        className={`${base} ${isDisabled ? disabledStyles : enabledStyles}`}
        onClick={() => {
          if (isDisabled) return;
          if (field) addFilter(field);
          setShowAddField(false);
        }}
      >
        <span>
          {field?.fieldID
            ? getColumnLabel(field.fieldLabel)
            : getColumnLabel(field.Field || field.column_name)}
        </span>

        {isDisabled && (
          <span className="ml-3 text-[10px] uppercase tracking-wide text-gray-400">
            Selected
          </span>
        )}
      </div>
    );
  })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SmartDropdown;